<template>
  <div class="transactions-view">
    <MainHeader title="Category Transactions" class="cat-transaction-header">
      <template #left>
        <font-awesome-icon
          @click="router.push({ name: 'Budget' })"
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
        />
      </template>
    </MainHeader>
    <div class="view">
      <div>
        <button
          class="blue-button mb-2 w-100"
          @click="router.push({ name: 'AddTransaction' })"
        >
          Add Transaction
        </button>
      </div>
      <div class="mb-2 w-100 flex">
        <input
          type="text"
          class="input"
          v-model="searchText"
          placeholder="Search Transaction"
          @keydown.enter="searchTransactions(searchText)"
        />
        <div class="search-icon-wrapper">
          <font-awesome-icon
            v-if="searchText && searchText.length > 0"
            :icon="['fas', 'circle-xmark']"
            class="search-icon pointer"
            @click="clearSearchText"
          />
          <font-awesome-icon
            v-else
            :icon="['fas', 'magnifying-glass-dollar']"
            class="search-icon pointer"
          />
        </div>
      </div>
      <div class="filter-section" ref="filterSection">
        <button
          v-for="(button, index) in state.buttons"
          :key="index"
          :class="{ 'active-filter': isActive(button) }"
          class="filter-button"
          @click="selectButton(button)"
        >
          {{ button }}
        </button>
      </div>
      <div v-if="filteredTransactions.length === 0" class="no-transactions">
        No transactions
      </div>
      <div v-for="item in filteredTransactions" :key="item">
        <div class="transactions-list" @click="editTransaction(item)">
          <div class="transaction-icon">
            <div class="icon-wrapper">
              <font-awesome-icon
                :icon="item.category.icon ? item.category.icon : ['fas', 'tag']"
                :style="{ color: item.category.iconColor }"
                class="subcategory-icon"
              />
            </div>
          </div>
          <div class="description">{{ item.description }}</div>
          <div
            class="amount text-right"
            :class="item.type === 'deposit' ? 'income-item' : 'expense-item'"
          >
            {{ formatMoney(item.amount) }}
          </div>
          <div class="transaction-grey institution">
            <div>
              {{
                item.category && item.category.name ? item.category.name : ''
              }}
            </div>
            <div>{{ item.institution }}</div>
          </div>
          <div class="text-right transaction-grey date">{{ item.date }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useMyStore } from '../store';
import { reactive, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import { formatMoney } from '../utils/formatMoney.js';
import MainHeader from '../components/View-Header.vue';

export default {
  components: { MainHeader },
  setup() {
    const store = useMyStore();
    const filterSection = ref(null);
    let searchText = ref('');

    const state = reactive({
      selectedButton: 'All',
      buttons: ['All', 'Today', 'Yesterday', 'This Week', 'Custom'],
    });

    let filteredTransactions = ref(store.transactions);

    const selectButton = (button) => {
      state.selectedButton = button;
      switch (button) {
        case 'All':
          filteredTransactions.value = store.transactions;
          break;
        case 'Today':
          filteredTransactions.value = store.transactions.filter(
            (transaction) => {
              const transactionDate = new Date(transaction.date);
              const today = new Date();
              return (
                transactionDate.getDate() === today.getDate() &&
                transactionDate.getMonth() === today.getMonth() &&
                transactionDate.getFullYear() === today.getFullYear()
              );
            }
          );
          break;
        case 'Yesterday':
          filteredTransactions.value = store.transactions.filter(
            (transaction) => {
              const transactionDate = new Date(transaction.date);
              const yesterday = new Date();
              yesterday.setDate(yesterday.getDate() - 1);
              return (
                transactionDate.getDate() === yesterday.getDate() &&
                transactionDate.getMonth() === yesterday.getMonth() &&
                transactionDate.getFullYear() === yesterday.getFullYear()
              );
            }
          );
          break;
        case 'This Week':
          filteredTransactions.value = store.transactions.filter(
            (transaction) => {
              const transactionDate = new Date(transaction.date);
              const today = new Date();
              const firstDayOfWeek = new Date(
                today.setDate(today.getDate() - today.getDay())
              );
              const lastDayOfWeek = new Date(
                today.setDate(today.getDate() - today.getDay() + 6)
              );
              return (
                transactionDate >= firstDayOfWeek &&
                transactionDate <= lastDayOfWeek
              );
            }
          );
          break;
        case 'Custom':
          // Logic to filter based on a custom date range
          break;
        default:
          filteredTransactions.value = store.transactions;
          break;
      }
    };

    const isActive = (button) => {
      return button === state.selectedButton;
    };

    const touch = { startX: 0, startY: 0, currentX: 0, currentY: 0 };

    const handleTouchStart = (event) => {
      touch.startX = event.touches[0].clientX;
      touch.startY = event.touches[0].clientY;
    };

    const handleTouchMove = (event) => {
      touch.currentX = event.touches[0].clientX;
      touch.currentY = event.touches[0].clientY;
      const { scrollLeft, scrollWidth, clientWidth } = filterSection.value;
      if (
        Math.abs(touch.currentX - touch.startX) > 1 &&
        Math.abs(touch.currentY - touch.startY) < 1
      ) {
        filterSection.value.scrollLeft =
          scrollLeft - touch.currentX + touch.startX;
        if (filterSection.value.scrollLeft < 3) {
          filterSection.value.scrollLeft = 0;
        } else if (filterSection.value.scrollLeft > scrollWidth - clientWidth) {
          filterSection.value.scrollLeft = scrollWidth - clientWidth;
        }
        event.preventDefault();
      }
    };

    const router = useRouter();

    const selectedOption = reactive({
      value: null,
    });

    const selectedDateOption = reactive({
      value: null,
    });

    const filterOptions = [
      { label: 'All (default)', value: 'All' },
      { label: 'Date', value: 'Date' },
      { label: 'Type', value: 'Type' },
      { label: 'Category', value: 'Category' },
    ];
    const dateFilterOptions = [
      { label: 'Week', value: 'Week' },
      { label: 'Month', value: 'Month' },
      { label: 'Range', value: 'Range' },
    ];
    const updateFilters = (option) => {
      selectedOption.value = option;
    };

    const updateDateFilters = (option) => {
      selectedDateOption.value = option;
    };

    function editTransaction(item) {
      store.updateEditTransaction(item);
      router.push({ name: 'EditTransaction' });
    }

    const clearSearchText = () => {
      searchText.value = '';
      searchTransactions('');
    };

    function searchTransactions(input) {
      const searchTerm = input.toLowerCase();
      filteredTransactions.value = store.transactions.filter((transaction) => {
        return Object.values(transaction).some((value) => {
          if (typeof value === 'object') {
            return Object.values(value).some((subValue) =>
              String(subValue).toLowerCase().includes(searchTerm)
            );
          } else {
            return String(value).toLowerCase().includes(searchTerm);
          }
        });
      });
      return filteredTransactions.value;
    }

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) {
          mainElement.scrollTo(0, 0);
        }
      }, 100);

      filterSection.value.addEventListener('touchstart', handleTouchStart);
      filterSection.value.addEventListener('touchmove', handleTouchMove);
    });

    onUnmounted(() => {
      if (filterSection.value) {
        filterSection.value.removeEventListener('touchstart', handleTouchStart);
        filterSection.value.removeEventListener('touchmove', handleTouchMove);
      }
    });

    return {
      store,
      filterOptions,
      updateFilters,
      selectedOption,
      dateFilterOptions,
      updateDateFilters,
      selectedDateOption,
      editTransaction,
      formatMoney,
      state,
      selectButton,
      isActive,
      filterSection,
      filteredTransactions,
      searchText,
      clearSearchText,
      searchTransactions,
      router,
    };
  },
};
</script>

<style scoped>
.transactions-list {
  display: grid;
  grid-template-columns: 15% 55% 30%;
  margin-bottom: 2em;
  cursor: pointer;
}

.search-icon-wrapper {
  font-size: 1em;
  box-sizing: border-box;
  background-color: #f2f2f2;
  color: #6e747a;
  opacity: 1;
  -webkit-text-fill-color: #6e747a;
  -webkit-opacity: 1;
  -webkit-background-color: #f2f2f2;
  padding: 0.6em 1.2em;
  border-bottom-left-radius: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  border: 1px solid transparent;
  font-weight: 500;
  font-family: inherit;
  text-align: center;
  line-height: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.search-icon {
  color: #6e747a;
  font-size: 1.3em;
  cursor: pointer;
}

.transactions-view .input {
  width: 100%;
  font-size: 1em;
  box-sizing: border-box;
  background-color: #f2f2f2;
  color: #6e747a;
  opacity: 1;
  -webkit-text-fill-color: #6e747a;
  -webkit-opacity: 1;
  -webkit-background-color: #f2f2f2;
  padding: 0.6em 1.2em;
  border-bottom-left-radius: 8px;
  border-top-left-radius: 8px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border: 1px solid transparent;
  font-weight: 500;
  font-family: inherit;
  text-align: center;
  line-height: 1;
}

.filter-section {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  gap: 0.5em;
  margin-bottom: 0.5em;
}

.filter-button {
  border-radius: 100px;
  background: white;
  border: 1px solid #d1d1d1;
  padding: 0.5em;
  white-space: nowrap;
  padding: 0.5em 1em;
  color: black;
}

.light-theme .active-filter {
  background: #004751;
  border: 1px solid #004751;
  color: white;
}

.dark-theme .active-filter {
  background: #4baffd;
  border: 1px solid #4baffd;
  color: white;
}

.no-transactions {
  height: 70vh;
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.transaction-icon {
  grid-row: 1 / span 2;
  display: flex;
  justify-content: start;
  align-items: center;
}

.transactions-view .icon-wrapper {
  position: relative;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.transaction-icon svg {
  display: block;
  position: relative;
  text-align: center;
  line-height: 30px;
}

.transactions-list .description {
  font-weight: 500;
  font-size: 1em;
}

.light-theme .transactions-list .description {
  color: #515a65;
}

.dark-theme .transactions-list .description {
  color: #ffffff;
}

.institution {
  font-size: 0.8em;
}

.date {
  font-size: 0.8em;
}

.transactions-list .amount {
  border-radius: 100px;
  border: none;
  font-weight: bold;
  font-weight: 600;
  font-size: 1em;
}

.light-theme .transactions-list .income-item {
  color: #86c24c;
}

.dark-theme .transactions-list .income-item {
  color: #89ca50;
}

.dark-theme .transactions-list .expense-item {
  color: #e3e2e2;
}

.light-theme .transaction-grey {
  color: #515a65;
}

.dark-theme .transaction-grey {
  color: #d4d2d2;
}
</style>
